import { makeStyles, tokens } from '@fluentui/react-components';
import { calculateRisk } from '../services/risk';

const useStyles = makeStyles({
  wrap: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    fontSize: '11.5px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  swatch: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  matrixContainer: {
    display: 'grid',
    gridTemplateColumns: '60px repeat(5, 1fr)',
    gridTemplateRows: '24px repeat(5, 38px)',
    gap: '2px',
    fontSize: '11px',
  },
  axisCorner: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    paddingRight: '4px',
    textAlign: 'right',
  },
  colHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '10.5px',
    color: tokens.colorNeutralForeground2,
  },
  rowHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '10.5px',
    color: tokens.colorNeutralForeground2,
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '11px',
  },
  axisLabelTop: {
    fontSize: '10px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: '4px',
  },
  axisLabelLeftWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  axisLabelLeft: {
    fontSize: '10px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    flexShrink: 0,
  },
  matrixSlot: { flex: 1 },
});

function cellBackground(score: number): string {
  if (score <= 2) return '#cdebd6';   // Low — light green
  if (score <= 6) return '#FBE5C9';   // Medium — light orange
  if (score <= 12) return '#F6BFB1';  // High — warm muted red
  return '#F1A4A6';                   // Extreme — light brand red
}

function cellTextColor(score: number): string {
  return score > 12 ? '#6F0F12' : '#231F20';
}

export function RiskMatrix() {
  const styles = useStyles();
  const consequenceLabels = ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic'];
  const likelihoodLabels = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost certain'];

  const cells: Array<{ row: number; col: number; score: number; level: string }> = [];
  for (let likeIdx = 0; likeIdx < 5; likeIdx++) {
    for (let consIdx = 0; consIdx < 5; consIdx++) {
      const likelihood = 5 - likeIdx;
      const consequence = consIdx + 1;
      const { score, level } = calculateRisk(likelihood, consequence);
      cells.push({ row: likeIdx, col: consIdx, score, level });
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: '#cdebd6' }} /> Low (1-2)
        </div>
        <div className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: '#FBE5C9' }} /> Medium (3-6)
        </div>
        <div className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: '#F6BFB1' }} /> High (7-12)
        </div>
        <div className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: '#F1A4A6' }} /> Extreme (13-25)
        </div>
      </div>

      <div className={styles.axisLabelTop}>Consequence →</div>

      <div className={styles.axisLabelLeftWrap}>
        <div className={styles.axisLabelLeft}>Likelihood →</div>
        <div className={styles.matrixSlot}>
          <div className={styles.matrixContainer}>
            <div className={styles.axisCorner}>L \\ C</div>
            {[1, 2, 3, 4, 5].map(c => (
              <div key={`col-${c}`} className={styles.colHeader} title={consequenceLabels[c - 1]}>
                {c}
              </div>
            ))}
            {[5, 4, 3, 2, 1].map(l => (
              <span key={`row-${l}`} style={{ display: 'contents' }}>
                <div className={styles.rowHeader} title={likelihoodLabels[l - 1]}>{l}</div>
                {[1, 2, 3, 4, 5].map(c => {
                  const cell = cells.find(x => x.row === 5 - l && x.col === c - 1)!;
                  return (
                    <div
                      key={`cell-${l}-${c}`}
                      className={styles.cell}
                      style={{
                        backgroundColor: cellBackground(cell.score),
                        color: cellTextColor(cell.score),
                      }}
                      title={`L=${l} × C=${c} = ${cell.score} ${cell.level}`}
                    >
                      {cell.score}
                    </div>
                  );
                })}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3, lineHeight: 1.5 }}>
        <strong>Likelihood (L):</strong> 1=Rare, 2=Unlikely, 3=Possible, 4=Likely, 5=Almost certain.
        <br />
        <strong>Consequence (C):</strong> 1=Negligible, 2=Minor, 3=Moderate, 4=Major, 5=Catastrophic.
        <br />
        Risk = L × C. Higher scores need more robust controls.
      </div>
    </div>
  );
}
